import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { faqListIcons } from 'common/constants/iconList'
import { HeadingTypeEnum } from 'common/enums/HeadingTypeEnum'
import { ContentTableInterface } from 'common/types/entities/ContentTableInterface'
import { Appearance } from 'common/types/entities/EntityInterface'
import React from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { RangeElement } from 'src/editor/shared/ui-lib/range-element'
import {
  OptionSingleValueType,
  SelectNew,
} from 'src/editor/shared/ui-lib/select-new/select-new'
import Choices from '../components/Choices'
import TextFontUpdater from '../components/Font/TextFontUpdater'
import IconPicker from '../components/IconPicker'
import IconPickerWithRemoveButton from '../components/IconPickerWithRemoveButton'
import Input from '../components/Input/Input'
import { useUpdateEntity, useUpdateProp } from '../hooks/useUpdateProps'
import DeviceAppearance from '../options/DeviceAppearance/DeviceAppearance'
import HtmlAttrId from '../options/HtmlAttrId'
import Border from '../styles/Border'
import FontSize from '../styles/FontSize'

const choices = [
  {
    label: 'H1',
    value: HeadingTypeEnum.HeaderOne,
  },
  {
    label: 'H2',
    value: HeadingTypeEnum.HeaderTwo,
  },
  {
    label: 'H3',
    value: HeadingTypeEnum.HeaderThree,
  },
  {
    label: 'H4',
    value: HeadingTypeEnum.HeaderFour,
  },
  {
    label: 'H5',
    value: HeadingTypeEnum.HeaderFive,
  },
  {
    label: 'H6',
    value: HeadingTypeEnum.HeaderSix,
  },
]

function ContentTableSettings({ entity }: { entity: ContentTableInterface }) {
  const updateProp = useUpdateProp(entity)
  const updateEntity = useUpdateEntity<ContentTableInterface>()
  const { isMobile } = useDeviceModeContext()
  const { t } = useTypedTranslation()

  const defaultStateOptions = [
    {
      label: t('entity_settings.default_state.closed'),
      value: false,
    },
    {
      label: t('entity_settings.default_state.opened'),
      value: true,
    },
  ]

  const getDefaultStateOption = (isOpen: boolean) => {
    const currentDefaultState = defaultStateOptions.find(
      state => state.value === isOpen,
    )
    if (currentDefaultState) {
      return currentDefaultState
      // biome-ignore lint/style/noUselessElse: <explanation>
    } else return defaultStateOptions[0]
  }

  function updateDefaultState(state: OptionSingleValueType<boolean>) {
    if (state) {
      const updatedState = state.value
      updateProp('isOpenedByDefault')(updatedState)
    }
  }

  function updateAppearance(appearance: Appearance) {
    updateEntity({
      ...entity,
      appearance,
    })
  }

  return (
    <>
      <GroupTitle isFirst>
        entity_settings.content_table.header.group_title
      </GroupTitle>
      <Input
        update={updateProp('headerTitle')}
        value={entity.headerTitle}
        label="entity_settings.content_table.header.text.label"
      />
      <FontSize
        labelText="entity_settings.content_table.header_font_size"
        update={updateProp('headerFontSize')}
        mobileUpdate={updateProp('headerFontSize')}
        fontSize={entity.headerFontSize}
        mobileFontSize={entity.headerFontSize}
      />
      <TextFontUpdater<ContentTableInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'headerFontFamily'}
        fontWeightKey={'headerFontWeight'}
        fontStyleKey={'headerFontStyle'}
        mobileFontFamilyKey={'headerFontFamily'}
        mobileFontWeightKey={'headerFontWeight'}
        mobileFontStyleKey={'headerFontStyle'}
        fontFileIdKey={'headerFontFileId'}
        mobileFontFileIdKey={'headerFontFileId'}
      />
      <ColorPickerControl
        update={updateProp('headerColor')}
        mobileUpdate={updateProp('headerColor')}
        color={entity.headerColor}
        mobileColor={entity.headerColor}
        label="entity_settings.content_table.header_color"
      />
      <IconPicker
        labelText="entity_options.icon_picker.title_collapsible_opened"
        iconList={faqListIcons}
        value={entity.iconClassName}
        update={updateProp('iconClassName')}
        searchPlaceholder="settings.components.icon_picker.placeholder"
      />
      <IconPickerWithRemoveButton
        label="entity_options.icon_picker.title_collapsible_closed"
        iconList={faqListIcons}
        value={entity.closedIconClassName}
        update={updateProp('closedIconClassName')}
      />
      <SelectNew
        labelText="entity_options.collapsible.default_state_title"
        selectedOption={
          typeof entity.isOpenedByDefault === 'boolean'
            ? getDefaultStateOption(entity.isOpenedByDefault)
            : defaultStateOptions[1]
        }
        onChange={updateDefaultState}
        options={defaultStateOptions}
        isMulti={false}
      />
      <ColorPickerControl
        update={updateProp('iconColor')}
        mobileUpdate={updateProp('iconColor')}
        color={entity.iconColor}
        mobileColor={entity.iconColor}
        label="entity_settings.content_table.icon_color"
      />
      <GroupTitle>entity_settings.content_table.content.group_title</GroupTitle>
      <Choices
        values={entity.headersTypes}
        choices={choices}
        update={updateProp('headersTypes')}
        label="entity_settings.content_table.headings_choices.label"
      />
      <FontSize
        labelText="entity_settings.content_table.content_font_size"
        update={updateProp('contentFontSize')}
        mobileUpdate={updateProp('contentFontSize')}
        fontSize={entity.contentFontSize}
        mobileFontSize={entity.contentFontSize}
      />
      <TextFontUpdater<ContentTableInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'contentFontFamily'}
        fontWeightKey={'contentFontWeight'}
        fontStyleKey={'contentFontStyle'}
        mobileFontFamilyKey={'contentFontFamily'}
        mobileFontWeightKey={'contentFontWeight'}
        mobileFontStyleKey={'contentFontStyle'}
        fontFileIdKey={'contentFontFileId'}
        mobileFontFileIdKey={'contentFontFileId'}
      />
      <RangeElement
        label="entity_settings.content_table.space_between_content_items"
        onChange={updateProp('contentItemGap')}
        value={entity.contentItemGap}
        max={20}
      />
      <ColorPickerControl
        update={updateProp('backgroundColor')}
        mobileUpdate={updateProp('mobileBackgroundColor')}
        color={entity.backgroundColor}
        mobileColor={entity.mobileBackgroundColor}
        label="entity_settings.content_table.background_color"
      />
      <ColorPickerControl
        update={updateProp('contentColor')}
        mobileUpdate={updateProp('contentColor')}
        color={entity.contentColor}
        mobileColor={entity.contentColor}
        label="entity_settings.content_table.content_color"
      />
      <Border border={entity.border} update={updateProp('border')} />
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <DeviceAppearance
        update={updateAppearance}
        desktop={entity.appearance.desktop}
        mobile={entity.appearance.mobile}
      />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}

export default ContentTableSettings
